import React from 'react';

// Actions
import { COMPLETE_QUESTIONNAIRE, refreshOmniRiskProfile } from '@/redux/modules/omni-risk-profiles';

// Assets
import { ReactComponent as EmailIcon } from '@/images/icons/omni-email.svg?react';
import { ReactComponent as EditIcon } from '@/images/icons/omni-edit.svg?react';

// Components
import { ModalCardButton } from '@/views/components/buttons';

// Emums
import { OmniRiskProfileStatus } from '@/enums/OmniRiskProfiles';

// Hooks
import { useLoadingUIState } from '@/hooks/useLoadingState';

// Libraries
import { Dispatch } from '@/redux';
import { startLoaderAction } from '@/redux/modules/ui/loaders';
import { useDispatch } from 'react-redux';

// Types
import { FormattedFullOmniRiskProfile } from '@/types/risk-profiles/omni/full-omni-risk-profile';

// Utils
import datadogUtils, { DATADOG_ACTIONS } from '@/utils/3rd-party/datadogUtils';
import { formatMessage } from '@/utils/i18n';

type Props = {
  riskProfile: FormattedFullOmniRiskProfile;
  sendQuestionnaireOnClick: () => void;
};

export const CreateQuestionnaire: React.FC<Props> = ({ riskProfile, sendQuestionnaireOnClick }) => {
  const { omniFormUrl, status } = riskProfile;

  const dispatch = useDispatch<Dispatch>();
  const isLoading = useLoadingUIState(COMPLETE_QUESTIONNAIRE.type);
  const isApproved = status === OmniRiskProfileStatus.Approved;
  const i18nPath = `RiskProfileModal.${isApproved ? 'Refresh' : 'Create'}Questionnaire`;
  const omniRedirectUrl = `${omniFormUrl}/review`;

  const handleCompleteQuestionnaire = async () => {
    dispatch(startLoaderAction(COMPLETE_QUESTIONNAIRE.type));
    if (isApproved) {
      await dispatch(refreshOmniRiskProfile(riskProfile));
    }

    datadogUtils.addAction(DATADOG_ACTIONS.RISK_PROFILE.CREATE_QUESTIONNAIRE.COMPLETE_QUESTIONNAIRE);

    window.location.href = omniRedirectUrl; // Redirect to OMNI Form for advisor to complete
  };

  return (
    <div className="create-questionnaire-content">
      <ModalCardButton
        icon={EmailIcon}
        title={formatMessage(`${i18nPath}.CardSendEmailTitle`)}
        dataTestId="createQuestionnaire-modalCardButton-sendQuestionnaire"
        description={formatMessage(`${i18nPath}.CardSendEmailDescription`)}
        onClick={sendQuestionnaireOnClick}
      />
      <ModalCardButton
        loading={isLoading}
        icon={EditIcon}
        title={formatMessage(`${i18nPath}.CardCompleteTitle`)}
        dataTestId="createQuestionnaire-modalCardButton-completeQuestionnaire"
        description={formatMessage(`${i18nPath}.CardCompleteDescription`)}
        onClick={handleCompleteQuestionnaire}
      />
    </div>
  );
};
